import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Tabs } from 'common/constants';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import { useFlagValue } from 'hooks';
import {
  useDeleteApproval,
  useUpdateInvoiceLockStatus,
  useUpdateInvoiceState
} from 'hooks/useClientDataServices';
import {
  AuthorizePaymentInvoice,
  LockStatus
} from 'pages/AuthorizePaymentPage/types';
import { setSelectedTab } from 'pages/LandingPage/components/LandingPageHeader/slice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  getSelectedInvoicesLength,
  setDueDate
} from '../components/VendorsTable/slice';
import { messages } from '../messages';
import type { DeclinePaymentResult } from '../types';

export const useDeclinePayment = (): DeclinePaymentResult => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');
  const selectedInvoicesLength = useAppSelector(getSelectedInvoicesLength);
  const { mutate: deleteApprovals, isLoading: isDeleteApprovalsLoading } =
    useDeleteApproval(() => {
      dispatch(setSelectedTab(Tabs.ToBePaid));
      dispatch(setDueDate(''));
    });
  const { mutate: updateInvoiceState, isLoading: isUpdateInvoiceStateLoading } =
    useUpdateInvoiceState();
  const { mutate: updateLockStatus, isLoading: isUnlockInvoicesLoading } =
    useUpdateInvoiceLockStatus();
  const onDeclineSuccess = useCallback(() => {
    dispatch(
      setSuccessMessage(
        intl.formatMessage(
          selectedInvoicesLength === 1
            ? messages.paymentDeclined
            : messages.paymentsDeclined
        )
      )
    );
    dispatch(setSelectedTab(Tabs.ToBePaid));
    dispatch(setDueDate(''));
  }, [dispatch, intl, selectedInvoicesLength]);
  const declinePayment = useCallback(
    (invoices: AuthorizePaymentInvoice[]) => {
      updateLockStatus(
        { lockStatus: LockStatus.UNLOCKED, invoices },
        {
          onSuccess: ({ successes }) => {
            if (landingPageFlag === true) {
              const vendorInvoicePairs = successes.map(
                ({ vendorNo, invoiceId }) => ({
                  vendorNo,
                  invoiceId
                })
              );
              const invoiceStatePayload = { vendorInvoicePairs };
              updateInvoiceState(
                {
                  state: 'POSTED',
                  payload: invoiceStatePayload
                },
                { onSuccess: onDeclineSuccess }
              );
            } else {
              deleteApprovals(successes, { onSuccess: onDeclineSuccess });
            }
          }
        }
      );
    },
    [
      deleteApprovals,
      landingPageFlag,
      onDeclineSuccess,
      updateInvoiceState,
      updateLockStatus
    ]
  );
  return {
    declinePayment,
    isLoading:
      isUnlockInvoicesLoading ||
      isDeleteApprovalsLoading ||
      isUpdateInvoiceStateLoading
  };
};

export default useDeclinePayment;
